import type { PartnerCockpitOfferRequest } from '@houzy/schemas/partner-cockpit/offer-requests';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackSnackbar } from '../../../components/FeedbackSnackbar';
import { ListPagination } from '../../../components/ListPagination';
import { useDeclineMatching } from '../api/useDeclineMatching';
import { useOfferRequestsList } from '../api/useOfferRequestsList';
import { DeclineOfferRequestDialog } from '../components/DeclineOfferRequestDialog';
import { OfferRequestsFiltersPanel } from '../components/OfferRequestsFiltersPanel';
import { OfferRequestsList } from '../components/OfferRequestsList';
import { OfferRequestsPageLayout } from '../components/OfferRequestsPageLayout';
import { pageRange, useOfferRequestsView } from '../hooks/useOfferRequestsView';

const VIEW_SESSION_KEY = 'new-requests.view';

export function NewOfferRequestsPage(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const view = useOfferRequestsView('new', VIEW_SESSION_KEY);
    const [pendingDecline, setPendingDecline] = useState<PartnerCockpitOfferRequest | null>(null);

    const query = useOfferRequestsList(view.query);
    const total = query.data?.total ?? 0;
    const offerRequests = query.data?.items ?? [];
    const declineMatching = useDeclineMatching();

    function confirmDecline(): void {
        if (declineMatching.isPending || pendingDecline === null) {
            return;
        }
        declineMatching.mutate(pendingDecline, { onSuccess: () => setPendingDecline(null) });
    }

    return (
        <>
            <OfferRequestsPageLayout
                title={t('OFFER_REQUESTS.NEW.TITLE')}
                filters={<OfferRequestsFiltersPanel value={view.filters} onChange={view.changeFilters} />}
                status={query.status}
                count={total}
                range={pageRange(view, total, offerRequests.length)}
                sort={view.sort}
                onSortChange={view.changeSort}
                footer={
                    total > 0 ? (
                        <ListPagination
                            page={view.page}
                            pageSize={view.pageSize}
                            total={total}
                            onPageChange={view.changePage}
                            onPageSizeChange={view.changePageSize}
                            itemsPerPageLabel={t('OFFER_REQUESTS.PAGINATION.ITEMS_PER_PAGE')}
                            paginationAriaLabel={t('OFFER_REQUESTS.PAGINATION.LABEL')}
                        />
                    ) : undefined
                }>
                <OfferRequestsList
                    offerRequests={offerRequests}
                    onSelectOfferRequest={(offerRequest) => {
                        void navigate({
                            to: '/new-requests/$offerRequestLeadUuid',
                            params: { offerRequestLeadUuid: offerRequest.uuid },
                        });
                    }}
                    onDeclineOfferRequest={setPendingDecline}
                    declineDisabled={declineMatching.isPending}
                />
            </OfferRequestsPageLayout>

            <DeclineOfferRequestDialog
                open={pendingDecline !== null}
                onConfirm={confirmDecline}
                onCancel={() => setPendingDecline(null)}
                confirmDisabled={declineMatching.isPending}
            />

            <FeedbackSnackbar
                open={declineMatching.isError}
                severity="error"
                message={t('OFFER_REQUESTS.DETAIL.DECLINE_ERROR')}
                onClose={() => declineMatching.reset()}
            />
        </>
    );
}
